import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { getBids, updateBidStatus } from '../api/api';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { CheckCircle, XCircle, Clock } from 'lucide-react';

const BidsAdminPage = () => {
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBids();
  }, []);

  const fetchBids = async () => {
    try {
      const data = await getBids();
      const sorted = data.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setBids(sorted);
    } catch (error) {
      console.error('Error loading bids:', error);
      toast.error('Failed to load bids');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (bidId, newStatus) => {
    try {
      await updateBidStatus(bidId, newStatus);
      toast.success(`Bid ${newStatus}!`);
      fetchBids();
    } catch (error) {
      console.error('Error updating bid status:', error);
      toast.error('Failed to update bid status');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: '#fef3c7', color: '#92400e', icon: <Clock size={16} /> },
      accepted: { bg: '#d1fae5', color: '#065f46', icon: <CheckCircle size={16} /> },
      rejected: { bg: '#fee2e2', color: '#991b1b', icon: <XCircle size={16} /> }
    };
    const style = styles[status] || styles.pending;
    
    return (
      <span 
        className="px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-2 inline-flex"
        style={{ background: style.bg, color: style.color }}
      >
        {style.icon}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="min-h-screen" style={{ background: '#f8f9fa' }}>
      <Navbar />
      <Toaster position="top-center" richColors />

      <section className="text-white py-16" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f4c75 100%)' }}>
        <div className="container mx-auto px-4">
          <h1 className="text-5xl font-bold mb-4">Bid Management</h1>
          <p className="text-xl" style={{ color: '#e8e8e8' }}>Review and manage property bids from customers</p>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center py-12"><p>Loading bids...</p></div>
          ) : bids.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-lg mb-4">No bids yet</p>
              <Link to="/properties"><Button className="btn-primary-custom rounded-full">View Properties</Button></Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 max-w-5xl mx-auto">
              {bids.map((bid) => (
                <Card key={bid.id} className="rounded-2xl border-2">
                  <CardContent className="p-6">
                    <div className="flex justify-between">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-2xl font-bold">{bid.property_name}</h3>
                          {getStatusBadge(bid.status)}
                        </div>
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <p className="text-sm font-semibold">Customer</p>
                            <p>{bid.customer_name}</p>
                            <p className="text-sm">{bid.customer_email}</p>
                          </div>
                          <div>
                            <p className="text-sm font-semibold">Bid: £{bid.bid_amount}/night</p>
                            <p className="text-2xl font-bold">£{bid.total_bid_amount.toFixed(2)}</p>
                          </div>
                        </div>
                        {bid.status === 'pending' && (
                          <div className="flex gap-2">
                            <Button onClick={() => handleStatusUpdate(bid.id, 'accepted')} className="bg-green-600 rounded-full">Accept</Button>
                            <Button onClick={() => handleStatusUpdate(bid.id, 'rejected')} variant="outline" className="border-red-500 text-red-500 rounded-full">Reject</Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BidsAdminPage;
